import { useContext, useState } from "react";
import { IdleContext } from "../providers/IdleProvider";
import { refreshAccessToken, handleSessionExpiration } from "../utils/token";
import { ForgeDialog, ForgeButton, ForgeScaffold, ForgeToolbar } from "@tylertech/forge-react";
import "./IdleDialog.css";

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const IdleDialog = () => {
  const { showIdleWarning, resetIdleState, remainingTime, logout, setIsRefreshingSession } =
    useContext(IdleContext);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await logout();
    } catch (error) {
      console.error("Failed to log out:", error);
      setIsLoading(false);
    }
  };

  const refreshAndResetSession = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    setIsRefreshingSession(true); // Prevent activity handler from closing dialog

    try {
      await refreshAccessToken();
      // Success - reset idle state and close dialog
      setIsRefreshingSession(false);
      resetIdleState();
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to refresh session:", error);

      // Token refresh failed - session is expired (typically after 2+ hours)
      // handleSessionExpiration() will dispatch an event that triggers immediate logout redirect
      console.warn("Token refresh failed during idle recovery, triggering session expiration");
      handleSessionExpiration();

      // Close the idle dialog - user will be redirected to logout/login immediately
      setIsRefreshingSession(false);
      resetIdleState();
      setIsLoading(false);
    }
  };

  const handleContinueSession = () => {
    refreshAndResetSession();
  };

  const handleDialogClose = () => {
    refreshAndResetSession();
  };

  return (
    <ForgeDialog
      className="idle-dialog"
      open={showIdleWarning}
      persistent={isLoading}
      on-forge-dialog-close={handleDialogClose}
      aria-labelledby="idle-dialog-title"
      aria-describedby="idle-dialog-message"
    >
      <ForgeScaffold>
        <ForgeToolbar slot="header">
          <h2 id="idle-dialog-title" slot="start" className="forge-typography--heading6">
            Are you still there?
          </h2>
        </ForgeToolbar>
        
        <div slot="body" className="idle-dialog-body">
          <div className="idle-dialog-content">
            <img 
              src="https://cdn.forge.tylertech.com/v1/images/spot/waving-spot.svg"
              alt="Idle Warning"
              className="idle-dialog-image"
            />
            <div className="idle-dialog-text">
              <p id="idle-dialog-message" className="forge-typography--body1">
                For your safety, your session will expire in{" "}
                <span className="idle-countdown">{formatTime(remainingTime)}</span>.{" "}
                Click "Continue Session" or close this dialog to keep your session active.
              </p>
            </div>
          </div>
          
          <div className="idle-dialog-actions">
            <ForgeButton
              variant="outlined"
              theme="primary"
              disabled={isLoading}
              onClick={handleLogout}
            >
              {isLoading ? "Logging out..." : "Logout Now"}
            </ForgeButton>
            
            <ForgeButton
              variant="raised"
              theme="primary"
              disabled={isLoading}
              onClick={handleContinueSession}
            >
              {isLoading ? "Continuing session..." : "Continue Session"}
            </ForgeButton>
          </div>
        </div>
      </ForgeScaffold>
    </ForgeDialog>
  );
};

export default IdleDialog;